"use client";

import React, { useState } from "react";
import type { ReviewsData } from "@/lib/googleReviews";
import { Star } from "lucide-react";

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${i <= Math.round(rating) ? "text-[#c3a953] fill-[#c3a953]" : "text-outline/30"}`}
        />
      ))}
    </span>
  );
}

function ReviewCard({
  author,
  avatar,
  rating,
  text,
  relativeTime,
  studioName,
}: ReviewsData["studios"][number]["reviews"][number] & { studioName: string }) {
  const [expanded, setExpanded] = useState(false);
  const long = text.length > 220;
  const shown = expanded || !long ? text : text.slice(0, 220) + "…";

  return (
    <div className="bg-white rounded-3xl p-6 border border-[#84979e]/20 flex flex-col gap-3">
      <div className="flex items-center gap-3">
        {avatar ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={avatar} alt={author} className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-primary-fixed flex items-center justify-center text-primary text-xs font-semibold">
            {author[0]}
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-on-surface">{author}</p>
          <p className="text-[11px] text-outline">{relativeTime} • {studioName}</p>
        </div>
      </div>
      <Stars rating={rating} />
      <p className="text-sm text-on-surface-variant font-light leading-relaxed">
        {shown}
        {long && (
          <button
            onClick={() => setExpanded((v) => !v)}
            className="ml-1 text-primary font-medium hover:underline"
          >
            {expanded ? "less" : "read more"}
          </button>
        )}
      </p>
    </div>
  );
}

export const GoogleReviews: React.FC<{ data: ReviewsData }> = ({ data }) => {
  const merged = data.studios.flatMap((s) =>
    s.reviews.map((r) => ({ ...r, studioName: s.studioName }))
  );

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center space-y-3 max-w-2xl mx-auto mb-10">
        <h2 className="font-headline font-semibold text-3xl sm:text-4xl text-on-surface">
          Loved by our Bali community
        </h2>
        <div className="flex items-center justify-center gap-3">
          <span className="font-headline font-semibold text-3xl text-primary">
            {data.overallRating.toFixed(1)}
          </span>
          <Stars rating={data.overallRating} />
          <span className="text-sm text-on-surface-variant">
            {data.totalCount.toLocaleString("id-ID")} Google reviews
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {merged.slice(0, 9).map((r, i) => (
          <ReviewCard key={i} {...r} />
        ))}
      </div>

      <div className="text-center mt-8">
        <a
          href="https://www.google.com/search?q=Sunset+Pilates+Bali+reviews"
          target="_blank"
          rel="noreferrer"
          className="text-xs font-bold uppercase tracking-wider text-primary hover:text-[#2c4e6e]"
        >
          Read all reviews on Google →
        </a>
      </div>
    </section>
  );
};
